import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { FileUpload } from 'primereact/fileupload';
import { useQuery, useMutation } from '@apollo/client';
import { z } from 'zod';
import yaml from 'js-yaml';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import { YamlEditor } from './YamlEditor';
import {
  GET_PORTFOLIO_NAME,
  UPDATE_PORTFOLIO,
  PortfolioSetInput,
  withUpdatedAt,
} from '~/graphql/queries/portfolio';
import { useAtomValue, useAtom } from 'jotai';
import { portfolioIdAtom } from '~/atoms/dashboardAtom';
import { toastAtom } from '~/atoms/toastAtom';
import { Toast } from 'primereact/toast';
import { currencyList } from '~/config/currenciesStatic';
import { isValidISIN } from '~/utils/validation';
import {
  endpointPortfolioDocumentUploadPOST,
  endpointPortfolioDocumentsPresignedUrlsGET,
  endpointPortfolioDocumentDelete,
  endpointPortfolioDocumentExtractionGET,
} from '~/api/def/endpoints/portfolioDocumentUploadEndpoint';
import { DocxViewer } from '~/components/ui/vendor/docx';

interface FormValues {
  fundName: string;
  fundCode: string;
  fundAum: string;
  fundCurrency: string;
  inceptionDate: Date | null;
  benchmarkIsin: string;
  investmentGuidelines: string;
}

// Get valid currency codes for validation
const validCurrencyCodes = currencyList.map((currency) => currency.value);

// Validation schema
const validationSchema = z.object({
  fundName: z
    .string()
    .min(2, 'Fund name must be at least 2 characters')
    .max(100, 'Fund name must be less than 100 characters'),
  fundCode: z
    .string()
    .min(2, 'Fund code must be at least 2 characters')
    .max(20, 'Fund code must be less than 20 characters'),
  fundAum: z
    .string()
    .optional()
    .refine((value) => {
      if (!value) return true; // Optional field
      return !isNaN(Number(value.replace(/,/g, '')));
    }, 'AUM must be a valid number'),
  fundCurrency: z.string().refine((val) => validCurrencyCodes.includes(val), {
    message: 'Please select a valid currency',
  }),
  inceptionDate: z
    .date()
    .nullable()
    .optional()
    .refine((date) => {
      if (!date) return true; // Optional field
      return date <= new Date();
    }, 'Inception date cannot be in the future'),
  benchmarkIsin: z
    .string()
    .optional()
    .refine((value) => {
      if (!value || value.trim() === '') return true;
      return isValidISIN(value);
    }, 'Invalid ISIN. Must be 12 characters with a valid check digit (e.g. US0378331005)'),
  investmentGuidelines: z.string().optional(),
});

// Custom validation function for Formik
const validateForm = (values: FormValues) => {
  try {
    validationSchema.parse(values);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldErrors: Record<string, string> = {};
      error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      return fieldErrors;
    }
    return {};
  }
};

export const EditPortfolioDialog = ({
  visible,
  onHide,
}: {
  visible: boolean;
  onHide: () => void;
}) => {
  const portfolioId = useAtomValue(portfolioIdAtom);
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const formikRef = useRef<HTMLDivElement>(null);

  // State for YAML validation
  const [jsonValidationError, setJsonValidationError] = useState<string | null>(
    null,
  );
  const [errorLine, setErrorLine] = useState<number | null>(null);

  // State for dialog messages
  const [dialogMessage, setDialogMessage] = useState<{
    severity: 'success' | 'info' | 'warn' | 'error';
    summary: string;
    detail: string;
  } | null>(null);

  // State for file upload and preview
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'pdf' | 'docx' | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState<string | null>(null);
  const [portfolioDocument, setPortfolioDocument] = useState<{
    success: boolean;
    portfolio_id: string;
    doc_uuid: string;
    original_name: string;
    presigned_url: string;
    file_type?: string;
  } | null>(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentPreviewError, setDocumentPreviewError] = useState<
    string | null
  >(null);
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const fileUploadRef = useRef<FileUpload>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const contentHeight = '72vh';

  // Helper function to detect file type
  const detectFileType = (
    fileName?: string,
    fileType?: string,
    mimeType?: string,
  ): { isPDF: boolean; isDOCX: boolean } => {
    const name = fileName?.toLowerCase() || '';
    const type = fileType?.toLowerCase() || '';
    const mime = mimeType?.toLowerCase() || '';

    const isPDF =
      name.endsWith('.pdf') || type === 'pdf' || mime === 'application/pdf';
    const isDOCX =
      name.endsWith('.docx') ||
      type === 'docx' ||
      mime ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    return { isPDF, isDOCX };
  };

  // Helper function to set document preview state
  const setDocumentPreview = (
    presignedUrl: string,
    fileName: string,
    fileType: 'pdf' | 'docx',
  ) => {
    if (fileType === 'pdf') {
      setPreviewUrl(presignedUrl);
      setFileType('pdf');
      setUploadedFile(new File([], fileName));
      setPresignedUrl(null);
    } else {
      setPresignedUrl(presignedUrl);
      setFileType('docx');
      setUploadedFile(new File([], fileName));
      setPreviewUrl(null);
    }
  };

  // Helper function to create document object from response
  const createDocumentFromResponse = (
    response: {
      success: boolean;
      portfolio_id?: string;
      doc_uuid?: string;
      document_name: string;
      presigned_url: string;
      document_type?: string;
    },
    portfolioId: string,
  ) => ({
    success: response.success,
    portfolio_id: response.portfolio_id || portfolioId,
    doc_uuid: response.doc_uuid || '',
    original_name: response.document_name,
    presigned_url: response.presigned_url,
    file_type: response.document_type,
  });

  // Helper function to handle document response and update preview
  const handleDocumentResponse = useCallback(
    async (
      documentResponse: {
        success: boolean;
        portfolio_id?: string;
        doc_uuid?: string;
        document_name?: string;
        presigned_url?: string;
        document_type?: string;
      },
      portfolioId: string,
    ) => {
      if (
        documentResponse.success &&
        documentResponse.presigned_url &&
        documentResponse.document_name
      ) {
        const doc = createDocumentFromResponse(
          {
            ...documentResponse,
            document_name: documentResponse.document_name,
            presigned_url: documentResponse.presigned_url,
          },
          portfolioId,
        );
        setPortfolioDocument(doc);

        const { isPDF, isDOCX } = detectFileType(
          documentResponse.document_name,
          documentResponse.document_type,
        );

        if (isPDF) {
          setDocumentPreview(
            documentResponse.presigned_url,
            documentResponse.document_name,
            'pdf',
          );
        } else if (isDOCX) {
          setDocumentPreview(
            documentResponse.presigned_url,
            documentResponse.document_name,
            'docx',
          );
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [],
  );

  const normalizeExtractedData = (data: unknown): string | null => {
    if (data === null || data === undefined) {
      return null;
    }

    // If it's already a string, try to parse it as JSON and convert to YAML
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return yaml.dump(parsed, { indent: 2, lineWidth: -1 });
      } catch {
        // If not valid JSON, return as-is (might already be YAML or plain text)
        return data;
      }
    }

    // Convert object/array to YAML
    try {
      return yaml.dump(data, { indent: 2, lineWidth: -1 });
    } catch (error) {
      console.error('Failed to convert extracted data to YAML', error);
      return String(data);
    }
  };

  // Validate YAML format for investment guidelines
  const validateYAML = (
    value: string,
  ): { message: string; line: number | null } | null => {
    if (!value || value.trim() === '') {
      return null; // Empty is OK
    }

    try {
      yaml.load(value);
      return null; // Valid YAML
    } catch (error) {
      const yamlError = error as yaml.YAMLException;
      // Get line number from yaml error (already 1-based from mark.line + 1 internally)
      const line =
        yamlError.mark?.line !== undefined ? yamlError.mark.line : null;

      // Create a user-friendly message
      let friendlyMessage = 'There is a formatting error in the YAML';
      if (line) {
        friendlyMessage = `Error on line ${line}`;

        // Add context based on common YAML errors
        const reason = yamlError.reason?.toLowerCase() || '';
        if (reason.includes('indent')) {
          friendlyMessage += ': Check the indentation (spaces)';
        } else if (reason.includes('colon')) {
          friendlyMessage += ': Missing colon after the key';
        } else if (reason.includes('mapping')) {
          friendlyMessage += ': Invalid structure - check spacing after colons';
        } else if (reason.includes('unexpected')) {
          friendlyMessage += ': Unexpected character found';
        } else if (yamlError.reason) {
          friendlyMessage += `: ${yamlError.reason}`;
        }
      }

      return { message: friendlyMessage, line };
    }
  };

  // Fetch portfolio data
  const { data: portfolioQueryData } = useQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId || !visible,
  });

  const fetchPortfolioDocument = useCallback(async () => {
    // Helper function to handle document response and update preview (moved inside to avoid dependency issues)
    const handleDocumentResponseLocal = async (
      documentResponse: {
        success: boolean;
        portfolio_id?: string;
        doc_uuid?: string;
        document_name?: string;
        presigned_url?: string;
        document_type?: string;
      },
      portfolioId: string,
    ) => {
      if (
        documentResponse.success &&
        documentResponse.presigned_url &&
        documentResponse.document_name
      ) {
        const doc = createDocumentFromResponse(
          {
            ...documentResponse,
            document_name: documentResponse.document_name,
            presigned_url: documentResponse.presigned_url,
          },
          portfolioId,
        );
        setPortfolioDocument(doc);

        const { isPDF, isDOCX } = detectFileType(
          documentResponse.document_name,
          documentResponse.document_type,
        );

        if (isPDF) {
          setDocumentPreview(
            documentResponse.presigned_url,
            documentResponse.document_name,
            'pdf',
          );
        } else if (isDOCX) {
          setDocumentPreview(
            documentResponse.presigned_url,
            documentResponse.document_name,
            'docx',
          );
        }
      }
    };

    if (!portfolioId || !visible) {
      setPortfolioDocument(null);
      setDocumentPreviewError(null);
      return;
    }

    setLoadingDocument(true);
    setDocumentPreviewError(null);
    setDocumentPreviewLoading(true);
    try {
      const response =
        await endpointPortfolioDocumentsPresignedUrlsGET(portfolioId);
      if (
        response.success &&
        response.presigned_url &&
        response.document_name
      ) {
        await handleDocumentResponseLocal(response, portfolioId);
        setDocumentPreviewError(null);
      } else {
        setPortfolioDocument(null);
        setDocumentPreviewLoading(false);
      }
    } catch (error) {
      console.error('Error fetching portfolio document:', error);
      setPortfolioDocument(null);
      setDocumentPreviewLoading(false);

      let statusCode: number | undefined;
      if (error && typeof error === 'object' && 'statusCode' in error) {
        statusCode = (error as { statusCode?: number }).statusCode;
      }

      if (statusCode === 404) {
        // No document exists yet for this portfolio; show upload UI without error
        setDocumentPreviewError(null);
      } else {
        setDocumentPreviewError(
          'Failed to load the latest document. Please try again or upload a new file.',
        );
      }
    } finally {
      setLoadingDocument(false);
    }
  }, [portfolioId, visible]);

  // Fetch portfolio document with presigned URL
  useEffect(() => {
    fetchPortfolioDocument();
  }, [fetchPortfolioDocument]);

  // Reset form state when dialog opens
  useEffect(() => {
    if (visible) {
      setDialogMessage(null);
      setJsonValidationError(null);
      // Reset file upload state
      setUploadedFile(null);
      setPreviewUrl(null);
      setPresignedUrl(null);
      setFileType(null);
      setExtractedData(null);
      setIsUploading(false);
      setIsDeleting(false);
      setIsExtracting(false);
      setPortfolioDocument(null);
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }

      // Validate initial guidelines if they exist (now as YAML)
      if (portfolioQueryData?.EQPortfolio?.[0]?.fund_type) {
        const fundType = portfolioQueryData.EQPortfolio[0].fund_type;
        // Convert to YAML for display validation
        const guidelinesYaml =
          typeof fundType === 'string'
            ? fundType
            : yaml.dump(fundType, { indent: 2, lineWidth: -1 });
        const validationResult = validateYAML(guidelinesYaml);
        setJsonValidationError(validationResult?.message || null);
        setErrorLine(validationResult?.line || null);
      }

      // Form will reset automatically due to enableReinitialize prop
    }
  }, [visible, portfolioQueryData]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Update portfolio mutation
  const [updatePortfolio, { loading: updateLoading }] = useMutation(
    UPDATE_PORTFOLIO,
    {
      refetchQueries: [GET_PORTFOLIO_NAME],
      onCompleted: () => {
        // Show success toast notification
        toastCurrent?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Portfolio updated successfully',
        });
        // Close dialog immediately after successful save
        onHide();
      },
      onError: (error) => {
        console.error('Error updating portfolio:', error);
        setDialogMessage({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update portfolio. Please try again.',
        });
      },
    },
  );

  // Initial form values
  const initialValues: FormValues = {
    fundName: '',
    fundCode: '',
    fundAum: '',
    fundCurrency: '',
    inceptionDate: null,
    benchmarkIsin: '',
    investmentGuidelines: '',
  };

  // Get initial values based on portfolio data
  // Converts JSON from server to YAML for display
  const getInitialValues = (): FormValues => {
    if (portfolioQueryData?.EQPortfolio?.[0]) {
      const portfolio = portfolioQueryData.EQPortfolio[0];

      // Convert fund_type to YAML for display
      let investmentGuidelinesYaml = '';
      if (portfolio.fund_type) {
        if (typeof portfolio.fund_type === 'string') {
          // If it's a string, try to parse as JSON first, then convert to YAML
          try {
            const parsed = JSON.parse(portfolio.fund_type);
            investmentGuidelinesYaml = yaml.dump(parsed, {
              indent: 2,
              lineWidth: -1,
            });
          } catch {
            // If not valid JSON, use as-is (might already be YAML or plain text)
            investmentGuidelinesYaml = portfolio.fund_type;
          }
        } else {
          // If it's an object, convert to YAML
          investmentGuidelinesYaml = yaml.dump(portfolio.fund_type, {
            indent: 2,
            lineWidth: -1,
          });
        }
      }

      return {
        fundName: portfolio.name || '',
        fundCode: portfolio.fund_code || '',
        fundAum: portfolio.aum ? portfolio.aum.toString() : '',
        fundCurrency: portfolio.base_currency || 'CHF',
        inceptionDate: portfolio.inception_date
          ? new Date(portfolio.inception_date)
          : null,
        benchmarkIsin: portfolio.benchmark_index || '',
        investmentGuidelines: investmentGuidelinesYaml,
      };
    }
    return initialValues;
  };

  const handleSubmit = (values: FormValues) => {
    // Prevent multiple submissions
    if (updateLoading) {
      return;
    }

    if (!portfolioId) {
      setDialogMessage({
        severity: 'error',
        summary: 'Error',
        detail: 'No portfolio selected. Please try again.',
      });
      return;
    }

    // Prevent submission if there's a YAML validation error
    if (jsonValidationError) {
      setDialogMessage({
        severity: 'error',
        summary: 'Validation Error',
        detail:
          'Please fix the YAML formatting error in Investment Guidelines before saving.',
      });
      return;
    }

    // Use extracted data if available, otherwise parse YAML guidelines
    // Convert YAML back to JSON/object for server storage
    // IMPORTANT: fund_type must NEVER be a string - it crashes the analysis process
    let fundTypeValue: object | null = null;

    // Priority: extracted data > manual input
    // Both extractedData and investmentGuidelines are in YAML format
    try {
      if (extractedData) {
        // Parse YAML extracted data back to object
        fundTypeValue = yaml.load(extractedData) as object;
      } else if (values.investmentGuidelines) {
        // Parse YAML and convert to object for server
        fundTypeValue = yaml.load(values.investmentGuidelines) as object;
      }
    } catch {
      // Parsing failed - validate and show error in the editor
      // The bad data is already visible in the editor, just trigger validation
      const rawContent = extractedData ?? values.investmentGuidelines;
      const validationResult = validateYAML(rawContent);
      setJsonValidationError(
        validationResult?.message ?? 'Invalid format - please check the data',
      );
      setErrorLine(validationResult?.line ?? null);
      return;
    }

    // Runtime validation: fund_type must be a plain object, never a primitive or array
    if (fundTypeValue !== null) {
      if (typeof fundTypeValue !== 'object' || Array.isArray(fundTypeValue)) {
        setJsonValidationError(
          'Investment guidelines must be a valid YAML object (key-value pairs), not a primitive value or array',
        );
        setDialogMessage({
          severity: 'error',
          summary: 'Validation Error',
          detail:
            'Investment guidelines must be structured as key-value pairs. Please check the format.',
        });
        return;
      }
    }

    const changes: PortfolioSetInput = withUpdatedAt({
      name: values.fundName,
      fund_code: values.fundCode,
      inception_date: values.inceptionDate?.toISOString().split('T')[0] || null,
      base_currency: values.fundCurrency,
      status: 'active',
      fund_type: fundTypeValue,
      aum: values.fundAum ? parseFloat(values.fundAum.replace(/,/g, '')) : null,
      benchmark_index: values.benchmarkIsin.trim().toUpperCase() || null,
    });

    updatePortfolio({
      variables: {
        id: portfolioId,
        changes,
      },
    });
  };

  const handleHide = () => {
    setDialogMessage(null);
    setJsonValidationError(null);
    // Reset file upload state
    setUploadedFile(null);
    // Only revoke blob URLs (from newly selected files), not presigned URLs (from existing documents)
    // This prevents the preview from disappearing before the dialog closes
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    // Don't clear preview URLs here - they'll be reset when dialog opens again
    // This prevents the preview from disappearing before the dialog closes
    setExtractedData(null);
    setIsUploading(false);
    setIsDeleting(false);
    setIsExtracting(false);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
    onHide();
  };

  const processFile = (file: File) => {
    // Validate file type
    const { isPDF, isDOCX } = detectFileType(file.name, undefined, file.type);

    if (!isPDF && !isDOCX) {
      setDialogMessage({
        severity: 'error',
        summary: 'Invalid File Type',
        detail: 'Please upload a PDF or DOCX file.',
      });
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
      return;
    }

    // Set file type first
    setFileType(isPDF ? 'pdf' : 'docx');

    // Create preview URL for PDF immediately
    if (isPDF) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setPresignedUrl(null);
    } else {
      // For DOCX, we cannot preview until the file is uploaded and we have a presigned URL
      // Clear any existing preview URLs
      setPreviewUrl(null);
      setPresignedUrl(null);
    }

    // Set uploaded file last to trigger preview render
    setUploadedFile(file);
    setDocumentPreviewError(null);
  };

  const handleFileSelect = (event: { files: File[] }) => {
    const file = event.files[0];
    if (!file) return;
    processFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDocumentLoadError = (message?: string) => {
    setDocumentPreviewError(
      message ||
        'Unable to load the document preview. Please try again or upload a new file.',
    );
    setDocumentPreviewLoading(false);
  };

  const handleDocumentLoadStart = () => {
    setDocumentPreviewLoading(true);
    setDocumentPreviewError(null);
  };

  const handleDocumentLoadSuccess = () => {
    setDocumentPreviewError(null);
    setDocumentPreviewLoading(false);
  };

  const handleDeleteDocument = async () => {
    if (!portfolioDocument?.doc_uuid) {
      setDialogMessage({
        severity: 'error',
        summary: 'Delete Error',
        detail: 'No document to delete.',
      });
      return;
    }

    setIsDeleting(true);
    setDialogMessage(null);

    try {
      const response = await endpointPortfolioDocumentDelete(
        portfolioDocument.doc_uuid,
      );

      if (response.success) {
        // Clear document state
        setPortfolioDocument(null);
        setPreviewUrl(null);
        setPresignedUrl(null);
        setFileType(null);
        setUploadedFile(null);
        setExtractedData(null);
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }

        setDialogMessage({
          severity: 'success',
          summary: 'Delete Successful',
          detail: 'Document deleted successfully.',
        });
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      let errorMessage = 'Failed to delete document. Please try again.';
      let errorDetail = '';

      if (error && typeof error === 'object' && 'statusCode' in error) {
        const apiError = error as {
          statusCode?: number;
          message?: string;
          response?: unknown;
        };

        if (apiError.statusCode) {
          errorDetail = `Status: ${apiError.statusCode}. `;
          switch (apiError.statusCode) {
            case 400:
              errorMessage =
                'Bad request. Please check the document and try again.';
              break;
            case 401:
              errorMessage =
                'Authentication failed. Please refresh and try again.';
              break;
            case 403:
              errorMessage =
                'Permission denied. You may not have access to delete documents.';
              break;
            case 404:
              errorMessage =
                'Document not found. It may have already been deleted.';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
          }

          if (apiError.response && typeof apiError.response === 'object') {
            const responseObj = apiError.response as Record<string, unknown>;
            if (typeof responseObj.message === 'string') {
              errorDetail += responseObj.message;
            } else if (typeof responseObj.error === 'string') {
              errorDetail += responseObj.error;
            }
          }
        } else if (apiError.message) {
          errorDetail = apiError.message;
        }
      } else if (error instanceof Error) {
        errorDetail = error.message;
      }

      setDialogMessage({
        severity: 'error',
        summary: 'Delete Error',
        detail: `${errorMessage} ${errorDetail}`.trim(),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReExtractData = async () => {
    if (!portfolioDocument?.doc_uuid) {
      setDialogMessage({
        severity: 'error',
        summary: 'Extraction Error',
        detail: 'No document to extract data from.',
      });
      return;
    }

    setIsExtracting(true);
    setDialogMessage(null);

    try {
      const response = await endpointPortfolioDocumentExtractionGET(
        portfolioDocument.doc_uuid,
      );

      if (response.success && response.extracted_data) {
        setExtractedData(normalizeExtractedData(response.extracted_data));
        setDialogMessage({
          severity: 'success',
          summary: 'Extraction Successful',
          detail: 'Data extracted successfully.',
        });
      } else {
        setDialogMessage({
          severity: 'warn',
          summary: 'Extraction Warning',
          detail: 'Extraction completed but no data was returned.',
        });
      }
    } catch (error) {
      console.error('Error extracting document data:', error);
      let errorMessage = 'Failed to extract document data. Please try again.';
      let errorDetail = '';

      if (error && typeof error === 'object' && 'statusCode' in error) {
        const apiError = error as {
          statusCode?: number;
          message?: string;
          response?: unknown;
        };

        if (apiError.statusCode) {
          errorDetail = `Status: ${apiError.statusCode}. `;
          switch (apiError.statusCode) {
            case 400:
              errorMessage =
                'Bad request. Please check the document and try again.';
              break;
            case 401:
              errorMessage =
                'Authentication failed. Please refresh and try again.';
              break;
            case 403:
              errorMessage =
                'Permission denied. You may not have access to extract data.';
              break;
            case 404:
              errorMessage = 'Document not found.';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
          }

          if (apiError.response && typeof apiError.response === 'object') {
            const responseObj = apiError.response as Record<string, unknown>;
            if (typeof responseObj.message === 'string') {
              errorDetail += responseObj.message;
            } else if (typeof responseObj.error === 'string') {
              errorDetail += responseObj.error;
            }
          }
        } else if (apiError.message) {
          errorDetail = apiError.message;
        }
      } else if (error instanceof Error) {
        errorDetail = error.message;
      }

      setDialogMessage({
        severity: 'error',
        summary: 'Extraction Error',
        detail: `${errorMessage} ${errorDetail}`.trim(),
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const dialogFooter = (
    <div className="flex justify-content-end align-items-center gap-2">
      <Button
        type="button"
        label="Cancel"
        icon="pi pi-times"
        severity="secondary"
        onClick={handleHide}
        disabled={updateLoading}
      />
      <Button
        label="Save Changes"
        icon="pi pi-check"
        type="submit"
        form="portfolio-form"
        autoFocus
        loading={updateLoading}
        disabled={updateLoading || !!jsonValidationError}
      />
    </div>
  );

  return (
    <>
      <Dialog
        header="Edit Portfolio"
        visible={visible}
        onHide={handleHide}
        style={{ width: '90vw', maxWidth: '1400px' }}
        contentStyle={{
          height: contentHeight,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        className="p-fluid"
        footer={dialogFooter}
        pt={{
          header: {
            style: {
              backgroundColor: 'var(--bq-surface-elevated)',
              color: 'var(--bq-text-primary)',
              padding: '1rem 1.5rem',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
            },
          },
          content: {
            style: {
              backgroundColor: 'var(--bq-surface-elevated)',
              color: 'var(--bq-text-secondary)',
              paddingBottom: '1rem',
            },
          },
          footer: {
            style: {
              backgroundColor: 'var(--bq-surface-elevated)',
              borderTop: '1px solid var(--bq-border-muted)',
              padding: '1rem 1.5rem',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            },
          },
        }}
      >
        <div
          ref={formikRef}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Formik
            initialValues={getInitialValues()}
            validate={validateForm}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              errors,
              touched,
              setFieldValue,
              values,
            }: FormikProps<FormValues>) => (
              <Form
                id="portfolio-form"
                className="dark-form"
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '1px',
                    backgroundColor: 'var(--bq-border-muted)',
                    margin: '0 -1.5rem',
                    width: 'calc(100% + 3rem)',
                  }}
                />

                {/* Dialog Message */}
                {dialogMessage && (
                  <div className="my-3" style={{ position: 'relative' }}>
                    <Message
                      severity={dialogMessage?.severity}
                      text={`${dialogMessage?.summary}: ${dialogMessage?.detail}`}
                      className="w-full"
                    />
                    <Button
                      type="button"
                      icon="pi pi-times"
                      className="p-button-text p-button-rounded p-button-plain"
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        width: '1.5rem',
                        height: '1.5rem',
                        padding: 0,
                        color: 'inherit',
                      }}
                      onClick={() => setDialogMessage(null)}
                      aria-label="Close message"
                    />
                  </div>
                )}

                <div
                  className="grid pt-3"
                  style={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'stretch',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  {/* Left Column - Form Fields */}
                  <div
                    className="col-12 md:col-6"
                    style={{
                      borderRight: '1px solid var(--bq-border-muted)',
                      paddingRight: '1.5rem',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <div className="grid" style={{ flexShrink: 0 }}>
                        <div className="col-12">
                          <div className="field">
                            <label
                              htmlFor="fundName"
                              className="font-bold block mb-2"
                            >
                              Fund Name
                            </label>
                            <Field
                              as={InputText}
                              id="fundName"
                              name="fundName"
                              placeholder="Portfolio Name"
                              className={`w-full ${errors.fundName && touched.fundName ? 'p-invalid' : ''}`}
                            />
                            <ErrorMessage
                              name="fundName"
                              component="small"
                              className="text-red-400"
                            />
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="field">
                            <label
                              htmlFor="fundCode"
                              className="font-bold block mb-2"
                            >
                              Fund Code
                            </label>
                            <Field
                              as={InputText}
                              id="fundCode"
                              name="fundCode"
                              placeholder="Portfolio Code"
                              className={`w-full ${errors.fundCode && touched.fundCode ? 'p-invalid' : ''}`}
                            />
                            <ErrorMessage
                              name="fundCode"
                              component="small"
                              className="text-red-400"
                            />
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="field">
                            <label
                              htmlFor="fundAum"
                              className="font-bold block mb-2"
                            >
                              Fund AUM
                            </label>
                            <Field
                              as={InputText}
                              id="fundAum"
                              name="fundAum"
                              placeholder="e.g., 2847300000"
                              className={`w-full ${errors.fundAum && touched.fundAum ? 'p-invalid' : ''}`}
                            />
                            <ErrorMessage
                              name="fundAum"
                              component="small"
                              className="text-red-400"
                            />
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="field">
                            <label
                              htmlFor="fundCurrency"
                              className="font-bold block mb-2"
                            >
                              Fund Currency
                            </label>
                            <Field
                              as={Dropdown}
                              id="fundCurrency"
                              name="fundCurrency"
                              options={currencyList}
                              placeholder="Select Currency"
                              className={`w-full ${errors.fundCurrency && touched.fundCurrency ? 'p-invalid' : ''}`}
                              onChange={(e: { value: string }) =>
                                setFieldValue('fundCurrency', e.value)
                              }
                              filter
                              filterBy="label,value"
                              showClear
                            />
                            <ErrorMessage
                              name="fundCurrency"
                              component="small"
                              className="text-red-400"
                            />
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="field">
                            <label
                              htmlFor="inceptionDate"
                              className="font-bold block mb-2"
                            >
                              Inception Date
                            </label>
                            <Field
                              as={Calendar}
                              id="inceptionDate"
                              name="inceptionDate"
                              placeholder="dd/mm/yyyy"
                              showIcon
                              className={`w-full ${errors.inceptionDate && touched.inceptionDate ? 'p-invalid' : ''}`}
                              onChange={(e: { value: Date | null }) =>
                                setFieldValue('inceptionDate', e.value)
                              }
                            />
                            <ErrorMessage
                              name="inceptionDate"
                              component="small"
                              className="text-red-400"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid mt-2">
                        <div className="col-6">
                          <div className="field">
                            <label
                              htmlFor="benchmarkIsin"
                              className="font-bold block mb-2"
                            >
                              Benchmark ISIN
                            </label>
                            <Field
                              as={InputText}
                              id="benchmarkIsin"
                              name="benchmarkIsin"
                              placeholder="e.g. US0378331005"
                              className={`w-full ${errors.benchmarkIsin && touched.benchmarkIsin ? 'p-invalid' : ''}`}
                            />
                            <small className="text-500">
                              Optional: ISIN of the benchmark index
                            </small>
                            <ErrorMessage
                              name="benchmarkIsin"
                              component="small"
                              className="text-red-400"
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          marginTop: '1rem',
                          width: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          className="field"
                          style={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            marginBottom: 0,
                            overflow: 'hidden',
                          }}
                        >
                          <label
                            htmlFor="investmentGuidelines"
                            className="font-bold block mb-2"
                          >
                            Investment Guidelines (YAML)
                          </label>

                          {/* YAML Validation Error */}
                          {jsonValidationError && (
                            <div className="mb-2" style={{ flexShrink: 0 }}>
                              <Message
                                severity="error"
                                text={jsonValidationError}
                                className="w-full"
                              />
                            </div>
                          )}

                          <div
                            style={{
                              flex: 1,
                              minHeight: 0,
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <YamlEditor
                              value={
                                extractedData !== null
                                  ? extractedData
                                  : values.investmentGuidelines
                              }
                              onChange={(newValue: string) => {
                                // Clear extracted data when user manually edits
                                if (
                                  extractedData !== null &&
                                  newValue !== extractedData
                                ) {
                                  setExtractedData(null);
                                }

                                // Validate YAML format
                                const validationResult = validateYAML(newValue);
                                setJsonValidationError(
                                  validationResult?.message || null,
                                );
                                setErrorLine(validationResult?.line || null);

                                // Update form value
                                setFieldValue('investmentGuidelines', newValue);
                              }}
                              placeholder="Enter investment guidelines in YAML format..."
                              hasError={!!jsonValidationError}
                              errorLine={errorLine}
                            />
                          </div>
                          <ErrorMessage
                            name="investmentGuidelines"
                            component="small"
                            className="text-red-400"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Drag & Drop, Preview, and Extracted Data */}
                  <div
                    className="col-12 md:col-6"
                    style={{
                      paddingLeft: '1.5rem',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      ref={dropZoneRef}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      className={`flex flex-column ${isDragOver ? 'bg-blue-900 bg-opacity-10' : ''}`}
                      style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                    >
                      {documentPreviewError && (
                        <div className="mb-3">
                          <Message
                            severity="error"
                            text={documentPreviewError}
                          />
                          {portfolioId && (
                            <div className="mt-2">
                              <Button
                                type="button"
                                label="Retry Loading Document"
                                icon="pi pi-refresh"
                                severity="secondary"
                                size="small"
                                disabled={loadingDocument}
                                onClick={() => fetchPortfolioDocument()}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {loadingDocument ? (
                        <div className="flex align-items-center justify-content-center h-full">
                          <div className="flex flex-column align-items-center gap-2">
                            <ProgressSpinner
                              style={{ width: '40px', height: '40px' }}
                            />
                            <span className="text-sm text-gray-400">
                              Loading document...
                            </span>
                          </div>
                        </div>
                      ) : portfolioDocument ? (
                        /* Show document preview when document exists */
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75rem',
                            height: '100%',
                          }}
                        >
                          {/* File Info and Action Buttons */}
                          <div
                            className="flex flex-column gap-2"
                            style={{ flexShrink: 0 }}
                          >
                            {/* First line: File name */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                width: '100%',
                              }}
                            >
                              <i
                                className={`pi ${
                                  fileType === 'pdf' ? 'pi-file-pdf' : 'pi-file'
                                } text-blue-500`}
                                style={{ flexShrink: 0, fontSize: '1.2rem' }}
                              />
                              <span
                                className="font-medium"
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: 'var(--bq-text-primary, #ffffff)',
                                }}
                              >
                                {portfolioDocument.original_name}
                              </span>
                            </div>
                            {/* Second line: Buttons */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                flexWrap: 'nowrap',
                              }}
                            >
                              {uploadedFile &&
                                uploadedFile.name !==
                                  portfolioDocument.original_name && (
                                  <Button
                                    type="button"
                                    icon="pi pi-upload"
                                    label="Upload"
                                    size="small"
                                    loading={isUploading}
                                    disabled={isUploading || !portfolioId}
                                    onClick={async () => {
                                      if (!uploadedFile || !portfolioId) {
                                        setDialogMessage({
                                          severity: 'error',
                                          summary: 'Upload Error',
                                          detail: 'Please select a file first.',
                                        });
                                        return;
                                      }

                                      setIsUploading(true);
                                      setDialogMessage(null);

                                      try {
                                        const response =
                                          await endpointPortfolioDocumentUploadPOST(
                                            portfolioId,
                                            uploadedFile,
                                          );

                                        if (
                                          response.success &&
                                          response.extracted_data
                                        ) {
                                          setExtractedData(
                                            normalizeExtractedData(
                                              response.extracted_data,
                                            ),
                                          );

                                          // Store presigned URL if available (for DOCX preview)
                                          if (response.presigned_url) {
                                            setPresignedUrl(
                                              response.presigned_url,
                                            );
                                          }

                                          // Refetch portfolio document to get the updated document
                                          try {
                                            const documentResponse =
                                              await endpointPortfolioDocumentsPresignedUrlsGET(
                                                portfolioId,
                                              );
                                            await handleDocumentResponse(
                                              documentResponse,
                                              portfolioId,
                                            );
                                          } catch (refetchError) {
                                            console.error(
                                              'Error refetching portfolio document:',
                                              refetchError,
                                            );
                                          }

                                          setDialogMessage({
                                            severity: 'success',
                                            summary: 'Upload Successful',
                                            detail:
                                              'Document uploaded successfully! Extracted data has been loaded into Investment Guidelines.',
                                          });
                                        } else if (response.success) {
                                          // Store presigned URL even if no extracted data
                                          if (response.presigned_url) {
                                            setPresignedUrl(
                                              response.presigned_url,
                                            );
                                          }

                                          // Refetch portfolio document to get the updated document
                                          try {
                                            const documentResponse =
                                              await endpointPortfolioDocumentsPresignedUrlsGET(
                                                portfolioId,
                                              );
                                            await handleDocumentResponse(
                                              documentResponse,
                                              portfolioId,
                                            );
                                          } catch (refetchError) {
                                            console.error(
                                              'Error refetching portfolio document:',
                                              refetchError,
                                            );
                                          }

                                          setDialogMessage({
                                            severity: 'warn',
                                            summary: 'Upload Warning',
                                            detail:
                                              'Document uploaded successfully but no extracted data was returned.',
                                          });
                                        } else {
                                          setDialogMessage({
                                            severity: 'error',
                                            summary: 'Upload Failed',
                                            detail:
                                              'Document upload was not successful. Please try again.',
                                          });
                                        }
                                      } catch (error) {
                                        console.error(
                                          'Error uploading document:',
                                          error,
                                        );

                                        // Extract detailed error information
                                        let errorMessage =
                                          'Failed to upload document. Please try again.';
                                        let errorDetail = '';

                                        // Check if it's a BQApiError with status code
                                        if (
                                          error &&
                                          typeof error === 'object' &&
                                          'statusCode' in error
                                        ) {
                                          const apiError = error as {
                                            statusCode?: number;
                                            response?: unknown;
                                            message?: string;
                                          };

                                          if (apiError.statusCode) {
                                            errorDetail = `Status: ${apiError.statusCode}. `;

                                            // Provide specific messages for common status codes
                                            switch (apiError.statusCode) {
                                              case 400:
                                                errorMessage =
                                                  'Bad request. Please check the file format and try again.';
                                                break;
                                              case 401:
                                                errorMessage =
                                                  'Authentication failed. Please refresh and try again.';
                                                break;
                                              case 403:
                                                errorMessage =
                                                  'Permission denied. You may not have access to upload documents.';
                                                break;
                                              case 413:
                                                errorMessage =
                                                  'File too large. Maximum file size is 5MB.';
                                                break;
                                              case 500:
                                                errorMessage =
                                                  'Server error. Please try again later.';
                                                break;
                                            }

                                            // Try to extract response message
                                            if (
                                              apiError.response &&
                                              typeof apiError.response ===
                                                'object'
                                            ) {
                                              const responseObj =
                                                apiError.response as Record<
                                                  string,
                                                  unknown
                                                >;
                                              if (
                                                typeof responseObj.message ===
                                                'string'
                                              ) {
                                                errorDetail +=
                                                  responseObj.message;
                                              } else if (
                                                typeof responseObj.error ===
                                                'string'
                                              ) {
                                                errorDetail +=
                                                  responseObj.error;
                                              }
                                            }
                                          } else if (apiError.message) {
                                            errorDetail = apiError.message;
                                          }
                                        } else if (error instanceof Error) {
                                          errorDetail = error.message;
                                        }

                                        setDialogMessage({
                                          severity: 'error',
                                          summary: 'Upload Error',
                                          detail:
                                            `${errorMessage} ${errorDetail}`.trim(),
                                        });
                                      } finally {
                                        setIsUploading(false);
                                      }
                                    }}
                                  />
                                )}
                              {!(
                                uploadedFile &&
                                uploadedFile.name !==
                                  portfolioDocument.original_name
                              ) && (
                                <Button
                                  type="button"
                                  icon="pi pi-upload"
                                  label="Upload New"
                                  size="small"
                                  loading={isUploading}
                                  disabled={
                                    isUploading ||
                                    isDeleting ||
                                    isExtracting ||
                                    !portfolioId
                                  }
                                  onClick={() => {
                                    // Trigger file picker by creating a temporary input and clicking it
                                    const input =
                                      document.createElement('input');
                                    input.type = 'file';
                                    input.accept =
                                      '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                                    input.onchange = (e) => {
                                      const target =
                                        e.target as HTMLInputElement;
                                      if (target.files && target.files[0]) {
                                        handleFileSelect({
                                          files: Array.from(target.files),
                                        });
                                      }
                                    };
                                    input.click();
                                  }}
                                />
                              )}
                              {!(
                                uploadedFile &&
                                uploadedFile.name !==
                                  portfolioDocument.original_name
                              ) && (
                                <>
                                  <Button
                                    type="button"
                                    icon="pi pi-refresh"
                                    label="Re-Process Data"
                                    size="small"
                                    severity="secondary"
                                    loading={isExtracting}
                                    disabled={
                                      isUploading ||
                                      isDeleting ||
                                      isExtracting ||
                                      !portfolioDocument?.doc_uuid
                                    }
                                    onClick={handleReExtractData}
                                  />
                                  <Button
                                    type="button"
                                    icon="pi pi-trash"
                                    label="Delete"
                                    className="w-26rem"
                                    size="small"
                                    severity="danger"
                                    loading={isDeleting}
                                    disabled={
                                      isUploading ||
                                      isDeleting ||
                                      isExtracting ||
                                      !portfolioDocument?.doc_uuid
                                    }
                                    onClick={handleDeleteDocument}
                                  />
                                </>
                              )}
                              {uploadedFile &&
                                uploadedFile.name !==
                                  portfolioDocument.original_name && (
                                  <Button
                                    type="button"
                                    icon="pi pi-times"
                                    label="Clear"
                                    size="small"
                                    severity="secondary"
                                    className="w-26rem"
                                    onClick={() => {
                                      setUploadedFile(null);
                                      setExtractedData(null);
                                      if (fileUploadRef.current) {
                                        fileUploadRef.current.clear();
                                      }

                                      // Restore original document preview if it exists
                                      if (portfolioDocument) {
                                        const { isPDF, isDOCX } =
                                          detectFileType(
                                            portfolioDocument.original_name,
                                            portfolioDocument.file_type,
                                          );

                                        if (
                                          isPDF &&
                                          portfolioDocument.presigned_url
                                        ) {
                                          setPreviewUrl(
                                            portfolioDocument.presigned_url,
                                          );
                                          setFileType('pdf');
                                          setPresignedUrl(null);
                                        } else if (
                                          isDOCX &&
                                          portfolioDocument.presigned_url
                                        ) {
                                          setPresignedUrl(
                                            portfolioDocument.presigned_url,
                                          );
                                          setFileType('docx');
                                          setPreviewUrl(null);
                                        }
                                      } else {
                                        // No original document, clear all preview state
                                        setPreviewUrl(null);
                                        setPresignedUrl(null);
                                        setFileType(null);
                                      }
                                    }}
                                  />
                                )}
                            </div>
                          </div>

                          {/* Preview Section */}
                          {fileType === 'pdf' && previewUrl && (
                            <div
                              style={{
                                flex: '1 1 0',
                                minHeight: '300px',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                overflow: 'hidden',
                              }}
                            >
                              {documentPreviewLoading && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    zIndex: 10,
                                  }}
                                >
                                  <div className="flex flex-column align-items-center gap-2">
                                    <ProgressSpinner
                                      style={{ width: '40px', height: '40px' }}
                                    />
                                    <span className="text-sm text-gray-400">
                                      Loading document...
                                    </span>
                                  </div>
                                </div>
                              )}
                              <iframe
                                src={`${previewUrl}#zoom=page-fit`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  border: 'none',
                                  flex: '1 1 0',
                                  minHeight: '250px',
                                  display: 'block',
                                }}
                                onLoadStart={handleDocumentLoadStart}
                                onLoad={handleDocumentLoadSuccess}
                                onError={() =>
                                  handleDocumentLoadError(
                                    'Unable to load the PDF preview. Please try again or re-upload the document.',
                                  )
                                }
                                title="PDF Preview"
                              />
                            </div>
                          )}

                          {fileType === 'docx' && presignedUrl ? (
                            <DocxViewer
                              url={presignedUrl}
                              onLoadStart={handleDocumentLoadStart}
                              onLoadSuccess={handleDocumentLoadSuccess}
                              onLoadError={(error) =>
                                handleDocumentLoadError(error)
                              }
                              style={{
                                flex: '1 1 0',
                                minHeight: '300px',
                                width: '100%',
                              }}
                            />
                          ) : fileType === 'docx' ? (
                            <div
                              className="flex-1"
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 0,
                                flex: '1 1 0',
                                padding: '2rem',
                              }}
                            >
                              <Card
                                className="bg-gray-800"
                                style={{
                                  maxWidth: '500px',
                                  width: '100%',
                                  border: '1px solid var(--bq-border-strong)',
                                }}
                                pt={{
                                  root: {
                                    style: {
                                      border:
                                        '1px solid var(--bq-border-strong)',
                                      borderRadius: '8px',
                                    },
                                  },
                                  body: {
                                    style: {
                                      padding: '2.5rem',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    },
                                  },
                                }}
                              >
                                <i
                                  className="pi pi-file"
                                  style={{
                                    fontSize: '3em',
                                    color: 'var(--bq-text-tertiary)',
                                    marginBottom: '1rem',
                                  }}
                                />
                                <p className="text-gray-400 text-center text-sm mb-2">
                                  {uploadedFile
                                    ? 'DOCX files can be previewed once uploaded. Please click "Upload" to upload the file and view the preview.'
                                    : 'Preview will be available once the file is uploaded.'}
                                </p>
                                {uploadedFile && (
                                  <p className="text-gray-500 text-center text-xs mt-2">
                                    Selected: {uploadedFile.name}
                                  </p>
                                )}
                              </Card>
                            </div>
                          ) : null}
                        </div>
                      ) : !uploadedFile ? (
                        /* Drag and Drop Area */
                        <Card
                          className="flex-1 flex align-items-center justify-content-center w-full"
                          pt={{
                            root: {
                              style: {
                                padding: '0rem',
                                height: '100%',
                                width: '100%',
                              },
                            },
                            body: {
                              style: {
                                backgroundColor: 'var(--bq-surface-elevated)',
                                padding: '2rem',
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: isDragOver
                                  ? '2px dashed var(--bq-color-info-strong)'
                                  : '2px dashed var(--bq-border-muted)',
                                borderRadius: '8px',
                              },
                            },
                          }}
                        >
                          <div className="text-center p-3 w-full">
                            <i
                              className={`pi ${isDragOver ? 'pi-cloud-upload' : 'pi-file'} text-3xl mb-2 ${isDragOver ? 'text-blue-400' : 'text-gray-400'}`}
                            />
                            <p className="text-gray-400 mb-2 font-semibold text-sm">
                              {isDragOver
                                ? 'Drop your file here!'
                                : 'Drag & Drop PDF/DOCX or Choose File'}
                            </p>
                            <p className="text-xs text-gray-600 mb-3">
                              Maximum file size: 5MB
                            </p>
                            <FileUpload
                              ref={fileUploadRef}
                              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              name="document"
                              customUpload
                              onSelect={handleFileSelect}
                              maxFileSize={5 * 1024 * 1024}
                              mode="basic"
                              chooseLabel="Choose File"
                              pt={{
                                basicButton: {
                                  style: {
                                    padding: '0.65625rem 1.09375rem',
                                  },
                                },
                              }}
                            />
                          </div>
                        </Card>
                      ) : (
                        /* Preview and Extracted Data when file is selected */
                        <div className="flex flex-column gap-3 h-full">
                          {/* File Info and Action Buttons */}
                          <div className="flex flex-column gap-2">
                            {/* First line: File name */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                width: '100%',
                              }}
                            >
                              <i
                                className={`pi ${
                                  fileType === 'pdf' ? 'pi-file-pdf' : 'pi-file'
                                } text-blue-500`}
                                style={{ flexShrink: 0, fontSize: '1.2rem' }}
                              />
                              <span
                                className="font-medium text-gray-300"
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {uploadedFile.name}
                              </span>
                            </div>
                            {/* Second line: Buttons */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                              }}
                            >
                              <Button
                                type="button"
                                icon="pi pi-upload"
                                label="Upload"
                                size="small"
                                loading={isUploading}
                                disabled={
                                  isUploading || !uploadedFile || !portfolioId
                                }
                                onClick={async () => {
                                  if (!uploadedFile || !portfolioId) {
                                    setDialogMessage({
                                      severity: 'error',
                                      summary: 'Upload Error',
                                      detail:
                                        'Please select a file and ensure a portfolio is selected.',
                                    });
                                    return;
                                  }

                                  setIsUploading(true);
                                  setDialogMessage(null);

                                  try {
                                    const response =
                                      await endpointPortfolioDocumentUploadPOST(
                                        portfolioId,
                                        uploadedFile,
                                      );

                                    if (
                                      response.success &&
                                      response.extracted_data
                                    ) {
                                      setExtractedData(
                                        normalizeExtractedData(
                                          response.extracted_data,
                                        ),
                                      );

                                      // Store presigned URL if available (for DOCX preview)
                                      if (response.presigned_url) {
                                        setPresignedUrl(response.presigned_url);
                                      }

                                      // Refetch portfolio document to get the updated document
                                      try {
                                        const documentResponse =
                                          await endpointPortfolioDocumentsPresignedUrlsGET(
                                            portfolioId,
                                          );
                                        await handleDocumentResponse(
                                          documentResponse,
                                          portfolioId,
                                        );
                                      } catch (refetchError) {
                                        console.error(
                                          'Error refetching portfolio document:',
                                          refetchError,
                                        );
                                      }

                                      setDialogMessage({
                                        severity: 'success',
                                        summary: 'Upload Successful',
                                        detail:
                                          'Document uploaded successfully! Extracted data has been loaded into Investment Guidelines.',
                                      });
                                    } else if (response.success) {
                                      // Store presigned URL even if no extracted data
                                      if (response.presigned_url) {
                                        setPresignedUrl(response.presigned_url);
                                      }

                                      // Refetch portfolio document to get the updated document
                                      try {
                                        const documentResponse =
                                          await endpointPortfolioDocumentsPresignedUrlsGET(
                                            portfolioId,
                                          );
                                        await handleDocumentResponse(
                                          documentResponse,
                                          portfolioId,
                                        );
                                      } catch (refetchError) {
                                        console.error(
                                          'Error refetching portfolio document:',
                                          refetchError,
                                        );
                                      }

                                      setDialogMessage({
                                        severity: 'warn',
                                        summary: 'Upload Warning',
                                        detail:
                                          'Document uploaded successfully but no extracted data was returned.',
                                      });
                                    } else {
                                      setDialogMessage({
                                        severity: 'error',
                                        summary: 'Upload Failed',
                                        detail:
                                          'Document upload was not successful. Please try again.',
                                      });
                                    }
                                  } catch (error) {
                                    console.error(
                                      'Error uploading document:',
                                      error,
                                    );

                                    // Extract detailed error information
                                    let errorMessage =
                                      'Failed to upload document. Please try again.';
                                    let errorDetail = '';

                                    // Check if it's a BQApiError with status code
                                    if (
                                      error &&
                                      typeof error === 'object' &&
                                      'statusCode' in error
                                    ) {
                                      const apiError = error as {
                                        statusCode?: number;
                                        response?: unknown;
                                        message?: string;
                                      };

                                      if (apiError.statusCode) {
                                        errorDetail = `Status: ${apiError.statusCode}. `;

                                        // Provide specific messages for common status codes
                                        switch (apiError.statusCode) {
                                          case 400:
                                            errorMessage =
                                              'Bad request. Please check the file format and try again.';
                                            break;
                                          case 401:
                                            errorMessage =
                                              'Authentication failed. Please refresh and try again.';
                                            break;
                                          case 403:
                                            errorMessage =
                                              'Permission denied. You may not have access to upload documents.';
                                            break;
                                          case 404:
                                            errorMessage =
                                              'Endpoint not found. Please check the server configuration.';
                                            break;
                                          case 413:
                                            errorMessage =
                                              'File too large. Please use a smaller file.';
                                            break;
                                          case 500:
                                            errorMessage =
                                              'Server error. Please try again later.';
                                            break;
                                          case 502:
                                          case 503:
                                            errorMessage =
                                              'Service unavailable. Please try again later.';
                                            break;
                                          default:
                                            errorMessage = `Upload failed with status ${apiError.statusCode}.`;
                                        }
                                      }

                                      // Try to extract response message
                                      if (apiError.response) {
                                        let responseData: string | undefined;

                                        if (
                                          typeof apiError.response === 'string'
                                        ) {
                                          responseData = apiError.response;
                                        } else if (
                                          apiError.response &&
                                          typeof apiError.response === 'object'
                                        ) {
                                          const responseObj =
                                            apiError.response as Record<
                                              string,
                                              unknown
                                            >;
                                          responseData =
                                            (typeof responseObj.message ===
                                            'string'
                                              ? responseObj.message
                                              : undefined) ||
                                            (typeof responseObj.error ===
                                            'string'
                                              ? responseObj.error
                                              : undefined) ||
                                            JSON.stringify(apiError.response);
                                        }

                                        if (
                                          responseData &&
                                          responseData !== errorMessage
                                        ) {
                                          errorDetail += responseData;
                                        }
                                      }

                                      if (
                                        apiError.message &&
                                        apiError.message !== 'Request failed'
                                      ) {
                                        errorDetail += apiError.message;
                                      }
                                    } else if (error instanceof Error) {
                                      errorMessage = error.message;
                                      // Check for network errors
                                      if (
                                        error.message.includes(
                                          'Network Error',
                                        ) ||
                                        error.message.includes(
                                          'Failed to fetch',
                                        )
                                      ) {
                                        errorMessage =
                                          'Network error. Please check your connection and ensure the server is running.';
                                        errorDetail =
                                          'Make sure the file extraction service is running at http://localhost:8000';
                                      }
                                    }

                                    setDialogMessage({
                                      severity: 'error',
                                      summary: 'Upload Error',
                                      detail: errorDetail
                                        ? `${errorMessage} ${errorDetail}`
                                        : errorMessage,
                                    });
                                  } finally {
                                    setIsUploading(false);
                                  }
                                }}
                              />
                              {uploadedFile && (
                                <Button
                                  type="button"
                                  icon="pi pi-times"
                                  label="Clear"
                                  severity="secondary"
                                  size="small"
                                  onClick={() => {
                                    setUploadedFile(null);
                                    if (previewUrl) {
                                      URL.revokeObjectURL(previewUrl);
                                      setPreviewUrl(null);
                                    }
                                    setFileType(null);
                                    if (fileUploadRef.current) {
                                      fileUploadRef.current.clear();
                                    }
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* Preview Section */}
                          <div
                            className="flex-1 flex flex-column"
                            style={{ minHeight: 0, overflow: 'hidden' }}
                          >
                            <label className="font-bold block">
                              Document Preview
                            </label>
                            <Card
                              className="bg-gray-900 flex-1"
                              style={{
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                              }}
                              pt={{
                                body: {
                                  style: {
                                    padding: 0,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                  },
                                },
                                content: {
                                  style: {
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    paddingBottom: 0,
                                    marginBottom: 0,
                                  },
                                },
                              }}
                            >
                              {fileType === 'pdf' && previewUrl ? (
                                <div
                                  style={{
                                    position: 'relative',
                                    height: '100%',
                                    width: '100%',
                                  }}
                                >
                                  {documentPreviewLoading && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        zIndex: 10,
                                      }}
                                    >
                                      <div className="flex flex-column align-items-center gap-2">
                                        <ProgressSpinner
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                          }}
                                        />
                                        <span className="text-sm text-gray-400">
                                          Loading document...
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  <iframe
                                    src={`${previewUrl}#zoom=page-fit`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      border: 'none',
                                      flex: 1,
                                    }}
                                    title="PDF Preview"
                                    onLoadStart={handleDocumentLoadStart}
                                    onLoad={handleDocumentLoadSuccess}
                                    onError={() =>
                                      handleDocumentLoadError(
                                        'Unable to load the PDF preview.',
                                      )
                                    }
                                  />
                                </div>
                              ) : fileType === 'docx' && presignedUrl ? (
                                <DocxViewer
                                  url={presignedUrl}
                                  onLoadStart={handleDocumentLoadStart}
                                  onLoadSuccess={handleDocumentLoadSuccess}
                                  onLoadError={(error) =>
                                    handleDocumentLoadError(error)
                                  }
                                  style={{
                                    flex: 1,
                                    minHeight: '300px',
                                    width: '100%',
                                  }}
                                />
                              ) : fileType === 'docx' ? (
                                <div className="p-3 text-center">
                                  <i
                                    className="pi pi-file"
                                    style={{
                                      fontSize: '3em',
                                      color: 'var(--bq-text-tertiary)',
                                      marginBottom: '1rem',
                                    }}
                                  />
                                  <p className="text-gray-400 text-sm">
                                    Preview will be available once the file is
                                    uploaded.
                                  </p>
                                </div>
                              ) : (
                                <div className="p-3 text-center">
                                  <ProgressSpinner
                                    style={{ width: '30px', height: '30px' }}
                                  />
                                  <p className="text-gray-400 mt-2">
                                    Loading preview...
                                  </p>
                                </div>
                              )}
                            </Card>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Dialog>
    </>
  );
};
